<template>
  <GenericCard
    :locked="locked"
    :subtitle="subtitle"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Child"
    :section="section"
    has-footer
    has-documents
    :document-types="documentTypes"
    :document-identifier="documentIdentifier"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <!-- Personal Info -->
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.first_name"
            autocomplete="off"
            label="First Name*"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.first_name)"
            @input="v$.section.first_name.$touch()"
            @blur="v$.section.first_name.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.middle_name"
            autocomplete="off"
            label="Middle Name"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.middle_name)"
            @input="v$.section.middle_name.$touch()"
            @blur="v$.section.middle_name.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.last_name"
            autocomplete="off"
            label="Last Name*"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.last_name)"
            @input="v$.section.last_name.$touch()"
            @blur="v$.section.last_name.$touch()"
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            v-model="section.gender_id"
            :items="optionsGenders"
            item-text="name"
            item-value="id"
            label="Gender*"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.gender_id)"
            @input="v$.section.gender_id.$touch()"
            @blur="v$.section.gender_id.$touch()"
          />
        </v-col>
      </v-row>
      <!-- Birth Info -->
      <v-row align="center">
        <v-col class="pl-4">
          <BirthDate
            :date="section.dob"
            :uncertain="section.uncertain_dob"
            required
            outlined
            :disabled="locked || partyIsLinked"
            for-party-edit
            @update-box="onUncertainDateUpdate"
            @update-date="onDoBUpdate"
            @tab-out="v$.section.dob.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.city_of_birth"
            autocomplete="off"
            label="City of Birth"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.city_of_birth)"
            @input="v$.section.city_of_birth.$touch()"
            @blur="v$.section.city_of_birth.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.state_of_birth"
            autocomplete="off"
            label="State/Province of Birth"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.state_of_birth)"
            @input="v$.section.state_of_birth.$touch()"
            @blur="v$.section.state_of_birth.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.birth_country_id"
            :items="optionsCountries"
            item-text="name"
            item-value="id"
            label="Country of birth"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.birth_country_id)"
            @input="v$.section.birth_country_id.$touch()"
            @blur="v$.section.birth_country_id.$touch()"
          />
        </v-col>
      </v-row>
      <!-- Residence & Process Info -->
      <v-row>
        <v-col cols="4">
          <v-autocomplete
            v-model="section.residence_country_id"
            :items="optionsCountries"
            item-text="name"
            item-value="id"
            label="Country of Residence"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.residence_country_id)"
            @input="v$.section.residence_country_id.$touch()"
            @blur="v$.section.residence_country_id.$touch()"
          />
        </v-col>
        <v-col cols="4">
          <v-row>
            <v-col>
              <v-text-field
                v-model="section.a_number"
                v-mask="aNumberMask"
                autocomplete="off"
                :label="aNumberLabel"
                outlined
                :disabled="locked || partyIsLinked || section.no_a_number"
                :error-messages="getErrorMessages(v$.section.a_number)"
                @input="v$.section.a_number.$touch()"
                @blur="v$.section.a_number.$touch()"
              />
            </v-col>
            <v-col cols="auto">
              <v-checkbox
                v-model="section.no_a_number"
                label="None"
                class="mt-3"
                :disabled="locked || partyIsLinked"
                @input="v$.section.no_a_number.$touch()"
                @blur="v$.section.no_a_number.$touch()"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="auto">
          <label for="deceased">Deceased?</label>
          <v-radio-group
            id="deceased"
            v-model="section.deceased"
            row
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.deceased)"
            class="mt-0"
            @input="v$.section.deceased.$touch()"
            @blur="v$.section.deceased.$touch()"
          >
            <v-radio
              label="No"
              :value="false"
            />
            <v-radio
              label="Yes"
              :value="true"
            />
          </v-radio-group>
        </v-col>
      </v-row>
      <!-- Immigration Info -->
      <v-row>
        <v-col cols="4">
          <v-autocomplete
            v-model="section.immigration_status"
            :items="optionsImmigrationStatuses"
            label="Immigration Status"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.immigration_status)"
            @input="v$.section.immigration_status.$touch()"
            @blur="v$.section.immigration_status.$touch()"
          />
        </v-col>
        <v-col
          v-if="fieldRules.immigration_status_other"
          cols="4"
        >
          <v-text-field
            v-model="section.immigration_status_other"
            autocomplete="off"
            label="Immigration Status Other"
            outlined
            :disabled="locked || partyIsLinked"
            :error-messages="getErrorMessages(v$.section.immigration_status_other)"
            @input="v$.section.immigration_status_other.$touch()"
            @blur="v$.section.immigration_status_other.$touch()"
          />
        </v-col>
      </v-row>
      <!-- Relation Info -->
      <v-row>
        <v-col cols="4">
          <v-autocomplete
            v-model="section.relation"
            :items="optionsRelations"
            label="Relation"
            outlined
            :error-messages="getErrorMessages(v$.section.relation)"
            @input="v$.section.relation.$touch()"
            @blur="v$.section.relation.$touch()"
          />
        </v-col>
      </v-row>

      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages(v$.section.notes)"
            @input="v$.section.notes.$touch()"
            @blur="v$.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>

    <template #extras>
      <v-btn
        v-if="!partyIsLinked"
        small
        class="ml-2"
        color="secondary"
        @click="linkPartyDialogOpen = true"
      >
        Link Party
      </v-btn>
    </template>

    <template #header>
      <v-chip
        v-if="partyIsLinked"
        class="ml-2 mr-2"
        color="primary"
        outlined
        label
      >
        <v-icon
          small
          left
        >
          mdi-exclamation
        </v-icon>
        Linked Party
      </v-chip>
      <template v-if="peUserCanUpdate">
        <v-btn
          v-if="partyIsLinked"
          small
          class="ml-2"
          color="warning"
          @click="unlinkDialog = true"
        >
          Unlink Party
        </v-btn>
        <v-btn
          v-if="partyIsLinked"
          small
          class="ml-2"
          color="secondary"
          @click="onEditParty"
        >
          Edit Party
        </v-btn>
        <v-btn
          v-if="!partyIsLinked"
          small
          class="ml-2"
          color="secondary"
          @click="onCreateParty"
        >
          Create Party
        </v-btn>
      </template>
    </template>

    <DialogLinkParty
      :status="linkPartyDialogOpen"
      :edited-party-id="peParty.id"
      @start-link="onStartLinkParty"
      @close="linkPartyDialogOpen = false"
    />

    <v-dialog
      v-model="unlinkDialog"
      persistent
      max-width="400"
    >
      <v-card>
        <v-card-title class="text-h5">
          Unlink Party
        </v-card-title>
        <v-card-text>Permanently unlink this party?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="unlinkDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary darken-1"
            text
            @click="onUnlinkParty"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogSelectRecordCase
      :status="createPartyDialogOpen"
      :party="peParty"
      :new-party="partyFullName"
      @continue="continueNewPartyCreation"
      @close="createPartyDialogOpen = false"
    />

    <CreateRecordDialog
      v-if="newRecordDialogOpen"
      is-create
      :open-locally="newRecordDialogOpen"
      :record-data="section"
      :redirect-after-save="false"
      @created="onRecordCreated"
      @close="newRecordDialogOpen = false"
    />

    <PEPartyAdversitiesDialog
      v-model="showAdversitiesDialog"
      :adversities="adversities"
      :on-proceed="onAdversityResolved"
    />

    <PEPartyDuplicatesDialog
      v-model="showDuplicatesDialog"
      :duplicates="duplicates"
      :on-proceed="onDuplicateResolved"
    />
  </GenericCard>
</template>

<script>
import { helpers, required, requiredUnless } from '@vuelidate/validators'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { MASKS, applyMask } from '@/utilities'
import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'
import { alertHandler } from '@/alertHandler'
import { immigrationStatusReqChecker } from '@/store/party/partials/party-edit/helpers/pe-immigration-status-card'
import { rules } from '@/utilities/validation-rules'

import BirthDate from '@/components/Fields/BirthDate.vue'
import CreateRecordDialog from '@/components/Dialogs/Record.vue'
import DialogLinkParty from '@/components/Dialogs/LinkParty.vue'
import DialogSelectRecordCase from '@/components/Dialogs/SelectRecordCase.vue'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'
import PEPartyAdversitiesDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyAdversitiesDialog.vue'
import PEPartyDuplicatesDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyDuplicatesDialog.vue'

export default {
  name: 'ChildrenCard',

  components: {
    BirthDate,
    CreateRecordDialog,
    DialogLinkParty,
    DialogSelectRecordCase,
    GenericCard,
    PEPartyAdversitiesDialog,
    PEPartyDuplicatesDialog,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      aNumberMask: MASKS.ANUMBER,
      adversities: [],
      cached_a_number: null,
      createPartyDialogOpen: false,

      documentTypes: [
        { text: 'Birth Certificate', value: 'birth-certificate' },
        { text: 'Death Certificate', value: 'death-certificate' },
        { text: 'National or State ID', value: 'national-or-state-id' },
        { text: 'Name Change Document', value: 'name-change-document' },
        { text: 'Immigration Status Document', value: 'immigration-status-document' },
      ],

      duplicates: [],
      linkPartyDialogOpen: false,
      newRecordDialogOpen: false,
      section: {},
      showAdversitiesDialog: false,
      showDuplicatesDialog: false,
      unlinkDialog: false,
    }
  },

  validations () {
    return {
      section: {
        a_number: { isAnumber: helpers.withMessage('A-number not valid', value => rules.aNumRules(value) === true) },
        birth_country_id: {},
        city_of_birth: {},
        created_at: {},
        deceased: {},
        dependent_of_id: {},
        dob: { required: requiredUnless(this.section.uncertain_dob) },
        first_name: { required: helpers.withMessage('First name is required', required) },
        gender_id: { required: helpers.withMessage('Gender is required', required) },
        immigration_status: {},
        immigration_status_other: {},
        income_verification: {},
        last_name: { required: helpers.withMessage('Last name is required', required) },
        linked_party_id: {},
        middle_name: {},
        monthly_income: {},
        no_a_number: {},
        notes: {},
        relation: {},
        residence_country_id: {},
        resides_with_id: {},
        state_of_birth: {},
        uncertain_dob: { required: requiredUnless(this.section.dob) },
        updated_at: {},
      },
    }
  },

  computed: {
    aNumberLabel () {
      return this.section.no_a_number ? 'A# (None)' : 'A#'
    },

    cardIdentifier () { return `children-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },
    documentIdentifier () { return `family-children-${this.section.id}` },
    fieldRules () { return immigrationStatusReqChecker(this.section.immigration_status) },

    locked: {
      get () {
        return this.peChildrenCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeChildrenCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsCountries () { return this.getCachedDropdown('countries') },
    optionsGenders () { return this.getCachedDropdown('genders') },
    optionsImmigrationStatuses () { return this.getCachedDropdown('immigration_statuses/immigration_status') },
    optionsRelations () { return this.getCachedDropdown('children/relation') },

    partyFullName () {
      const headerString = [this.section.first_name]
      if (this.section.middle_name) headerString.push(this.section.middle_name)
      headerString.push(this.section.last_name)

      return headerString.join(' ')
    },

    partyIsLinked () { return this.section.linked_party_id !== null },
    sectionIdentifier () { return this.section.id || 'new' },
    subtitle () { return `(${this.partyFullName})` },

    ...mapGetters(PARTY_NAMESPACE, [
      'peChildrenCardLock',
      'peParty',
      'peUserCanUpdate',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      deep: true,

      handler: function () {
        this.section = { ...this.item }
        if (this.section.a_number) this.section.a_number = applyMask(this.section.a_number, MASKS.ANUMBER)
      },

      immediate: true,
    },

    'section.no_a_number' (val) {
      if (val) {
        this.cached_a_number = this.section.a_number
        this.$nextTick().then(_ => { this.section.a_number = '' })
      } else if (this.cached_a_number) {
        this.section.a_number = this.cached_a_number
        this.$nextTick().then(_ => { this.cached_a_number = null })
      }
    },
  },

  created () {
    if (!this.create) this.initPeChildrenCard(this.sectionIdentifier)
    this.getDropdownOptions({ options: false, path: 'countries' })
    this.getDropdownOptions({ options: false, path: 'genders' })
    this.getDropdownOptions({ path: 'children/relation' })
    this.getDropdownOptions({ path: 'immigration_statuses/immigration_status' })
  },

  methods: {
    async continueNewPartyCreation (data) {
      if (data.new) {
        this.section.pcId = this.section.id
        this.newRecordDialogOpen = true
      } else {
        const party = await this.peFamilySectionCreateParty({
          legalCaseId: data.case,
          party: this.section,
          recordId: data.record,
        })
        this.section.linked_party_id = party.id
        await this.onCardUpdate()
        this.displayAlert('success', 'Party created successfully')
      }
    },

    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onAdversityResolved (forceCreationComment) {
      this.showAdversitiesDialog = false
      this.section.adversity_resolution_comment = forceCreationComment

      this.createPartyDialogOpen = true
    },

    async onCardCreate () {
      return this.peChildrenCardCreate({
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onCardDelete () {
      if (this.create) return this.peChildrenCardCreateUndo()
      else {
        return this.peChildrenCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peChildrenCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peChildrenCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onCreateParty () {
      this.duplicates = await this.detectDuplicate({
        first_name: this.section.first_name,
        last_name: this.section.last_name,
      })

      if (this.duplicates.length > 0) this.showDuplicatesDialog = true
      else this.onDuplicateResolved()
    },

    onDoBUpdate (date) {
      this.v$.section.dob.$touch()
      this.v$.section.uncertain_dob.$touch()
      this.section.dob = date
    },

    async onDuplicateResolved () {
      this.showDuplicatesDialog = false

      this.adversities = await this.detectAdversity({
        first_name: this.section.first_name,
        last_name: this.section.last_name,
      })

      if (this.adversities.length > 0) this.showAdversitiesDialog = true
      else this.onAdversityResolved()
    },

    onEditParty () {
      this.$router.push({ name: 'partyEdit', params: { id: String(this.section.linked_party_id) } })
    },

    onMarriageDateUncertainUpdate (value) {
      this.v$.section.marriage_date.$touch()
      this.v$.section.marriage_date_uncertain.$touch()
      this.section.marriage_date_uncertain = value
    },

    onMarriageDateUpdate (date) {
      this.v$.section.marriage_date.$touch()
      this.v$.section.marriage_date_uncertain.$touch()
      this.section.marriage_date = date
    },

    onRecordCreated (record) {
      this.section.linked_party_id = record.record_holder_party_id
      this.onCardUpdate().
        then(_ => this.displayAlert('success', 'Party created successfully'))
    },

    async onSectionUnlock () {
      return this.setPeChildrenCardLock({ index: this.sectionIdentifier, locked: false })
    },

    onStartLinkParty (partyLinkId) {
      this.section.linked_party_id = partyLinkId
      this.linkPartyDialogOpen = false

      const nextAction = this.section.id ? this.onCardUpdate : this.onCardCreate
      nextAction().
        then(_ => this.displayAlert('success', 'Party linked successfully'))
    },

    onTerminationDateUncertainUpdate (value) {
      this.v$.section.termination_date.$touch()
      this.v$.section.termination_date_uncertain.$touch()
      this.section.termination_date_uncertain = value
    },

    onTerminationDateUpdate (date) {
      this.v$.section.termination_date.$touch()
      this.v$.section.termination_date_uncertain.$touch()
      this.section.termination_date = date
    },

    onUncertainDateUpdate (value) {
      this.v$.section.dob.$touch()
      this.v$.section.uncertain_dob.$touch()
      this.section.uncertain_dob = value
    },

    onUnlinkParty () {
      this.section.linked_party_id = null
      this.unlinkDialog = false
      this.onCardUpdate().
        then(_ => this.displayAlert('success', 'Party unlinked successfully'))
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peChildrenCardCreateUndo',
      'setPeChildrenCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'detectAdversity',
      'detectDuplicate',
      'initPeChildrenCard',
      'peChildrenCardCreate',
      'peChildrenCardDelete',
      'peChildrenCardRefresh',
      'peChildrenCardUpdate',
      'peFamilySectionCreateParty',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
